import { getI18n } from "@/plugins/i18n"
import moment from "moment"
import 'moment/dist/locale/nl'
import 'moment/dist/locale/tr'
import * as sHelpers from "./string-helpers"

/**
 * Tarihi belirtilen formata göre formatlar.
 * @param {Date|string} d - Formatlanacak tarih
 * @param {string} f - Tarih formatı (varsayılan: "DD/MM/YYYY HH:mm")
 * @returns {string} - Formatlanmış tarih
 * format(new Date("2023-10-26T10:00:00"))	"26/10/2023 10:00"
 * format("2023-10-26", "DD.MM.YYYY")	"26.10.2023"
 */
export const format = (d, f = "DD/MM/YYYY HH:mm") =>
  d && moment(d).isValid() && moment(d).format(f)

/**
 * Tarihin geçmişte olup olmadığını kontrol eder.
 * @param {Date|string} d - Karşılaştırılacak tarih
 * @returns {boolean} - Tarihin geçmişte olup olmadığını
 * dateIsPast(new Date("2023-10-26"))	true (eğer bugün 26 Ekim 2023'ten sonraysa)
 * dateIsPast("2023-10-26")	true (eğer bugün 26 Ekim 2023'ten sonraysa)
 */
export const dateIsPast = d =>
  moment(d).isValid() && moment().diff(moment(d)) > 0

/**
 * Belirli bir dildeki tüm ayların adlarını alır.
 * @param {string} lang - Dil kodu (varsayılan: "en")
 * @returns {string[]} - Dildeki tüm ayların adları
 * getMonths("tr")	["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"]
 * getMonths("en")	["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
 */
export const getMonths = (lang = "en") =>
  moment.localeData(lang).months().map(sHelpers.capitalize)

/**
 * Türkçe ayların adlarını alır.
 * @param {string} lang - Dil kodu (varsayılan: "tr")
 * @returns {string[]} - Türkçe ayların adları
 * getMonthsTr("tr")	["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"]
 * getMonthsTr("en")	["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
 */
export const getMonthsTr = (lang = "tr") =>
  moment.localeData(lang).months().map(sHelpers.capitalize)

/**
 * Saat stringini dakikaya çevirir.
 * @param {string} timeStr - Saat stringi (örn: "13:30")
 * @returns {number} - Dakika sayısını döndürür
 * toMinutes("13:30")	810
 * toMinutes("09:00")	540
 */
export const toMinutes = timeStr => {
  const [hour, minute] = timeStr.split(":").map(Number)
  
  return hour * 60 + minute
}

const getActiveLocale = (providedLocale) => {
  if (providedLocale && typeof providedLocale === 'string') return providedLocale
  try {
    const i18n = getI18n()
    const currentLocale = i18n?.global?.locale?.value || i18n?.global?.locale || 'tr'
    return typeof currentLocale === 'string' ? currentLocale : 'tr'
  } catch (e) {
    return 'tr'
  }
}

/**
 * Tarihi kısa formatta (gün-ay) formatlar (i18n destekli).
 * @param {Date|string} date - Formatlanacak tarih
 * @param {string} [locale] - Dil kodu (varsayılan: aktif i18n dili)
 * @returns {string} - Formatlanmış tarih
 * formatDateShortI18n("2023-10-26")	"26 Eki"
 * formatDateShortI18n("2023-10-26", "en")	"26 Oct"
 */
export const formatDateShortI18n = (date, locale) => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const activeLocale = getActiveLocale(locale)
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[activeLocale] || activeLocale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "short",
  }).format(dt)
}

/**
 * Tarihi formatlar (i18n destekli).
 * @param {Date|string} date - Formatlanacak tarih
 * @param {string} [locale] - Dil kodu (varsayılan: aktif i18n dili)
 * @returns {string} - Formatlanmış tarih
 * formatDateI18n("2023-10-26")	"26.10.2023"
 * formatDateI18n("2023-10-26", "en")	"26/10/2023"
 */
export const formatDateI18n = (date, locale) => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const activeLocale = getActiveLocale(locale)
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[activeLocale] || activeLocale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(dt)
}

/**
 * Tarihi ve saati formatlar (i18n destekli).
 * @param {Date|string} date - Formatlanacak tarih
 * @param {string} [locale] - Dil kodu (varsayılan: aktif i18n dili)
 * @returns {string} - Formatlanmış tarih
 * formatDateTimeI18n("2023-10-26T10:00:00")	"26.10.2023 10:00"
 * formatDateTimeI18n("2023-10-26T10:00:00", "en")	"26/10/2023 10:00"
 */
export const formatDateTimeI18n = (date, locale) => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const activeLocale = getActiveLocale(locale)
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[activeLocale] || activeLocale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt)
}

/**
 * Saati formatlar (i18n destekli).
 * @param {Date|string} date - Formatlanacak tarih
 * @param {string} [locale] - Dil kodu (varsayılan: aktif i18n dili)
 * @returns {string} - Formatlanmış tarih
 * formatTimeI18n("2023-10-26T10:00:00")	"10:00"
 * formatTimeI18n("2023-10-26T10:00:00", "en")	"10:00"
 */
export const formatTimeI18n = (date, locale) => {
  if (!date) return null
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const activeLocale = getActiveLocale(locale)
  const localeMap = {
    'tr': 'tr-TR',
    'en': 'en-GB',
    'nl': 'nl-NL',
    'ar': 'ar-SA',
  }

  const mappedLocale = localeMap[activeLocale] || activeLocale || 'tr-TR'

  return new Intl.DateTimeFormat(mappedLocale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(dt)
}

/**
 * E-posta listesi için tarihi i18n destekli dinamik formatlar.
 * Bugün ise: saat (10:00)
 * Bu yıl ise: kısa tarih (26 Eki / 26 Oct)
 * Farklı yıl ise: tarih (26.10.2023 / 26/10/2023)
 * @param {Date|string} date 
 * @param {string} [locale] 
 * @returns {string}
 */
export const formatMailDateI18n = (date, locale) => {
  if (!date) return ''
  const dt = new Date(date)
  if (isNaN(dt)) return ''

  const now = new Date()
  const isToday = dt.toDateString() === now.toDateString()
  const isSameYear = dt.getFullYear() === now.getFullYear()

  if (isToday) {
    return formatTimeI18n(dt, locale)
  } else if (isSameYear) {
    return formatDateShortI18n(dt, locale)
  } else {
    return formatDateI18n(dt, locale)
  }
}


/**
 * Tarihi formatlar (Türkçe format).
 * @param {Date|string} date - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih
 * formatDateTrTarih("2023-10-26")	"26.10.2023"
 * formatDateTrTarih("2023-10-26")	"26.10.2023"
 */
export const formatDateTrTarih = date => {
  if (date && moment(date).isValid()) {
    return moment(date).format("DD.MM.YYYY")
  }
  
  return null
}

/**
 * Tarihi ve saati formatlar (Türkçe format).
 * @param {Date|string} date - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih
 * formatDateTrTarihSaat("2023-10-26T10:00:00")	"26.10.2023 10:00"
 * formatDateTrTarihSaat("2023-10-26T10:00:00")	"26.10.2023 10:00"
 */
export const formatDateTrTarihSaat = date => {
  if (date && moment(date).isValid()) {
    return moment(date).format("DD.MM.YYYY HH:mm")
  }
  
  return null
}

/**
 * Tarihi ve gün adını formatlar (i18n destekli).
 * @param {Date|string} date - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih
 * formatDateTrTarihGun("2023-10-26")	"26.10.2023 Perşembe"
 * formatDateTrTarihGun("2023-10-26")	"26.10.2023 Perşembe"
 */
export const formatDateTrTarihGun = date => {
  if (!date) return null

  const locale = getI18n().global.locale.value || "tr"
  const dt = new Date(date)
  if (isNaN(dt)) return null

  const day = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(dt)

  const formattedDate = dt.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })

  return `${formattedDate} ${day}`
}

/**
 * Tarihi formatlar (YYYY-MM-DD).
 * @param {Date|string} date - Formatlanacak tarih
 * formatDate("2023-10-26")	"2023-10-26"
 * formatDate("26.10.2023")	"2023-10-26"
 */
export const formatDate = date => {
  let parsed = moment(date, "DD.MM.YYYY")
  if (parsed.isValid()) {
    return moment(parsed).format("YYYY-MM-DD")
  } else {
    return null
  }
}

/**
 * Tarihi formatlar (dd/mm/yyyy).
 * @param {Date|string} value - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih
 * myDate("2023-10-26")	"26/10/2023"
 * myDate("26.10.2023")	"26/10/2023"
 */
export const myDate = value => {
  if (value) {
    return moment(String(value)).format("dd/mm/yyyy")
  }
}

/**
 * Tarihi ay adı ve yıl formatında formatlar (i18n destekli).
 * @param {Date|string} value - Formatlanacak tarih
 * @returns {string} - Formatlanmış tarih
 * formatDateAyAdYil("2023-10-26")	"Ekim 2023"
 * formatDateAyAdYil("2023-10-26", "en")	"October 2023"
 */
export const formatDateAyAdYil = value => {
  if (value) {
    const i18n = getI18n()
    let locale = i18n.global.locale.value || i18n.global.locale || "tr"
    if (typeof locale !== 'string') locale = "tr"
    
    moment.locale(locale)
    
    return moment(String(value)).format("MMMM YYYY")
  }
}

/**
 * Yaşı hesaplar.
 * @param {string} dateString - Doğum tarihi (DD/MM/YYYY formatında)
 * @returns {number} - Yaş
 * calculateAge("26/10/1990")	33
 * calculateAge("01/01/2000")	23
 */
export const calculateAge = dateString => {
  const birthDate = moment(dateString, "DD/MM/YYYY")

  if (!birthDate.isValid()) {
    return null
  }

  return moment().diff(birthDate, "years")
}
